<template>
  <div class="gpr-list">
    <!-- État vide -->
    <div v-if="modelValue.length === 0" class="empty-state">
      <p class="empty-state-text">Aucune ligne GPR pour l'instant.</p>
      <p class="empty-state-hint">Cliquez sur <strong>+ Ajouter GPR</strong> ci-dessus pour en ajouter une. <em>(Optionnel)</em></p>
    </div>

    <!-- Liste des lignes -->
    <div v-else class="lines-table">
      <div
        v-for="(line, index) in modelValue"
        :key="index"
        class="line-row gpr-row"
        @click="openEditModal(index)"
        title="Cliquer pour modifier"
      >
        <!-- Badge numéro -->
        <span class="line-badge line-badge-gpr">{{ getLineNumber(index) }}</span>

        <!-- Infos principales -->
        <div class="line-info">
          <div class="line-info-primary">
            <span v-if="line.precoTravauxGPR" class="info-chip">
              <i class="fas fa-hard-hat chip-icon"></i>
              {{ line.precoTravauxGPR }}
            </span>
            <span v-else class="info-chip info-chip-empty">
              <i class="fas fa-minus chip-icon"></i>
              Préco non définie
            </span>

            <span v-if="line.travauxGPRValidesEDF" class="info-chip" :class="line.travauxGPRValidesEDF === 'OUI' ? 'info-chip-success' : 'info-chip-warning'">
              <i class="fas fa-bolt chip-icon"></i>
              EDF : {{ line.travauxGPRValidesEDF }}
            </span>
          </div>

          <div class="line-info-secondary">
            <!-- Durée -->
            <span v-if="line.dureePrevTravauxGPR != null" class="info-pill">
              <span class="pill-label">Durée</span>
              <span class="pill-value">{{ line.dureePrevTravauxGPR }} j</span>
            </span>

            <!-- Coûts -->
            <span v-if="line.coutTravauxGPR != null" class="info-pill">
              <span class="pill-label">Travaux</span>
              <span class="pill-value">{{ formatCost(line.coutTravauxGPR) }} €</span>
            </span>
            <span v-if="line.coutDAATGPR != null" class="info-pill">
              <span class="pill-label">DAAT</span>
              <span class="pill-value">{{ formatCost(line.coutDAATGPR) }} €</span>
            </span>
            <span v-if="line.coutDiagElecGPR != null" class="info-pill">
              <span class="pill-label">Diag Elec</span>
              <span class="pill-value">{{ formatCost(line.coutDiagElecGPR) }} €</span>
            </span>

            <!-- Sinistre -->
            <span v-if="line.sinistre === 'oui'" class="info-pill pill-warning">
              <i class="fas fa-exclamation-triangle chip-icon"></i>
              <span class="pill-value">Sinistre</span>
            </span>

            <!-- Photos -->
            <span v-if="line.photos && line.photos.length > 0" class="info-pill pill-photo">
              <i class="fas fa-camera chip-icon"></i>
              <span class="pill-value">{{ line.photos.length }} photo{{ line.photos.length > 1 ? 's' : '' }}</span>
            </span>
          </div>
        </div>

        <!-- Actions -->
        <div class="line-actions" @click.stop>
          <button
            type="button"
            class="action-btn action-edit"
            @click="openEditModal(index)"
            title="Modifier"
          >
            <i class="fas fa-pencil-alt"></i>
          </button>
          <button
            type="button"
            class="action-btn action-delete"
            @click="removeLine(index)"
            title="Supprimer"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modale d'édition GPR -->
    <ModalGPR
      v-if="editingModal !== null"
      :line="editingModal.line"
      :isEditing="editingModal.isEditing"
      @confirm="onModalConfirm"
      @cancel="onModalCancel"
    />
  </div>
</template>

<script>
import ModalGPR from './ModalGPR.vue'

export default {
  name: 'FormGPR',

  components: { ModalGPR },

  props: {
    modelValue: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  emits: ['update:modelValue'],

  data() {
    return {
      // { line: Object|null, index: Number|null, isEditing: Boolean }
      editingModal: null
    }
  },

  methods: {
    getLineNumber(index) {
      const num = index + 1
      if (num === 1) return '1ère'
      if (num === 2) return '2ème'
      return `${num}ème`
    },

    formatCost(value) {
      if (value == null) return null
      return Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    openNewModal() {
      this.editingModal = {
        line: null,
        index: null,
        isEditing: false
      }
    },

    openEditModal(index) {
      this.editingModal = {
        line: this.modelValue[index],
        index,
        isEditing: true
      }
    },

    onModalConfirm(line) {
      const newLines = [...this.modelValue]
      if (this.editingModal.isEditing && this.editingModal.index !== null) {
        newLines[this.editingModal.index] = line
      } else {
        newLines.push(line)
      }
      this.$emit('update:modelValue', newLines)
      this.editingModal = null
    },

    onModalCancel() {
      this.editingModal = null
    },

    removeLine(index) {
      const newLines = this.modelValue.filter((_, i) => i !== index)
      this.$emit('update:modelValue', newLines)
    },

    // Exposé au parent pour ouvrir la modale depuis le bouton "Ajouter GPR"
    addLine() {
      this.openNewModal()
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.gpr-list {
  width: 100%;
}

/* ── État vide ─────────────────────────────────────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.75rem 1rem;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  text-align: center;
  gap: 0.35rem;
}

.empty-state-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.empty-state-hint {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;

  em {
    color: #9ca3af;
    font-style: normal;
  }
}

/* ── Table des lignes ─────────────────────────────────────────────────────── */
.lines-table {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.line-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #bfdbfe;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
    background: #f0f7ff;
  }
}

.gpr-row {
  border-left: 3px solid #3b82f6;
}

/* Badge numéro */
.line-badge {
  flex-shrink: 0;
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.line-badge-gpr {
  background: #dbeafe;
  color: #1e40af;
}

/* Infos */
.line-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.line-info-primary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.line-info-secondary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
}

.info-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  background: #dbeafe;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #1e40af;
  white-space: nowrap;
}

.info-chip-empty {
  background: #f3f4f6;
  color: #9ca3af;
}

.info-chip-success {
  background: #dcfce7;
  color: #166534;
}

.info-chip-warning {
  background: #fff7ed;
  color: #9a3412;
}

.chip-icon {
  font-size: 0.7rem;
  opacity: 0.75;
}

.info-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.15rem 0.45rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;

  .pill-label {
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.68rem;
    letter-spacing: 0.2px;
    flex-shrink: 0;
  }

  .pill-value {
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.pill-warning {
  background: #fffbf0;
  border-color: #fcd34d;

  .chip-icon, .pill-value { color: #b45309; }
}

.pill-photo {
  background: #f0f9ff;
  border-color: #bae6fd;

  .chip-icon, .pill-value { color: #0284c7; }
}

/* Actions */
.line-actions {
  display: flex;
  gap: 0.35rem;
  flex-shrink: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background 0.15s, transform 0.1s;

  &:active { transform: scale(0.93); }
}

.action-edit {
  background: #e0f2fe;
  color: #0284c7;

  &:hover { background: #bae6fd; }
}

.action-delete {
  background: #fee2e2;
  color: #dc2626;

  &:hover { background: #fecaca; }
}
</style>
